import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { SERVER } from '../../../../config/config';
import { usePermission } from '../../../../hooks/usePermission';
import VideoManager from '../../../../components/VideoManager';
import { 
  FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, 
  FaSave, FaTimes, FaImage, FaSearch, FaExternalLinkAlt,
  FaStar, FaLayerGroup, FaVideo
} from 'react-icons/fa';

const Portfolio = () => {
  const { can } = usePermission();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [managingSections, setManagingSections] = useState(null);
  const [managingVideos, setManagingVideos] = useState(null);
  const [projectSections, setProjectSections] = useState([]);
  const [projectVideos, setProjectVideos] = useState([]);
  const [sectionVideos, setSectionVideos] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [videosLoading, setVideosLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    subtitle: '',
    description: '',
    detailed_description: '',
    client_name: '',
    project_year: new Date().getFullYear(),
    project_duration: '',
    project_url: '',
    category: 'Branding',
    tags: [],
    status: 'completed',
    featured: false,
    order_index: 0,
    active: true,
    cover_image: null,
    existing_cover_image: '',
    home_image: null, // Add this
    existing_home_image: '', // Add this
    gallery: [],
    existing_gallery: [],
    deliverables: [],
    testimonials: []
  });

  const [sectionFormData, setSectionFormData] = useState({
    section_title: '',
    section_description: '',
    section_url: '',
    order_index: 0,
    gallery: [],
    existingGallery: [], // For existing images from server
    highlights: [],
    section_tags: []
  });

  const canEdit = can('content.edit');
  const canCreate = can('content.create');
  const canDelete = can('content.delete');

  const categories = ['Branding', 'Website', 'Mobile Apps', 'Graphic Design', 'Content Creation'];
  const statuses = ['completed', 'in_progress', 'draft'];

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...(filterCategory !== 'all' && { category: filterCategory }),
        ...(filterStatus !== 'all' && { status: filterStatus })
      };

      const response = await axios.get(`${SERVER}/portfolio/admin/list`, {
        params,
        withCredentials: true
      });

      setProjects(response.data.projects || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterCategory, filterStatus]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate && !editingProject) return;
    if (!canEdit && editingProject) return;

    try {
      const formDataToSend = new FormData();
      
      // Append basic fields
      Object.keys(formData).forEach(key => {
        if (key === 'tags' || key === 'deliverables' || key === 'testimonials') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (key === 'cover_image') {
          if (formData[key] instanceof File) {
            formDataToSend.append('gallery', formData[key]); // Cover image goes first in gallery
          }
        } else if (key === 'home_image') {
          if (formData[key] instanceof File) {
            formDataToSend.append('gallery', formData[key]); // Home image goes second in gallery
          }
        } else if (key === 'gallery') {
          if (Array.isArray(formData[key])) {
            formData[key].forEach(file => {
              if (file instanceof File) {
                formDataToSend.append('gallery', file);
              }
            });
          }
        } else if (key === 'existing_cover_image') {
          // Send existing cover image path if no new cover image
          if (formData[key] && !(formData.cover_image instanceof File)) {
            formDataToSend.append('existing_cover_image', formData[key]);
          }
        } else if (key === 'existing_home_image') {
          // Send existing home image path if no new home image
          if (formData[key] && !(formData.home_image instanceof File)) {
            formDataToSend.append('existing_home_image', formData[key]);
          }
        } else if (key === 'existing_gallery') {
          // Send existing gallery paths if no new gallery images
          if (formData[key] && formData[key].length > 0 && (!formData.gallery || formData.gallery.length === 0)) {
            formDataToSend.append('existing_gallery', JSON.stringify(formData[key]));
          }
        } else if (key !== 'cover_image' && key !== 'home_image' && key !== 'gallery' && key !== 'existing_cover_image' && key !== 'existing_home_image' && key !== 'existing_gallery') {
          formDataToSend.append(key, formData[key]);
        }
      });

      let response;
      if (editingProject) {
        response = await axios.put(`${SERVER}/portfolio/admin/${editingProject.id}`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.post(`${SERVER}/portfolio/admin`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (response.status === 200 || response.status === 201) {
        fetchProjects();
        closeModal();
      }
    } catch (error) {
      console.error('Error saving project:', error);
      const errorMessage = error.response?.data?.error || 'Error saving project. Please try again.';
      alert(errorMessage);
    }
  };
  const handleEdit = async (project) => {
    if (!canEdit) return;
    
    try {
      const response = await axios.get(`${SERVER}/portfolio/admin/${project.id}`, {
        withCredentials: true
      });
      
      const projectData = response.data;
      console.log('Fetched project data for editing:', projectData); // Debug log
      
      setFormData({
        title: projectData.title || '',
        slug: projectData.slug || '',
        subtitle: projectData.subtitle || '',
        description: projectData.description || '',
        detailed_description: projectData.detailed_description || '',
        client_name: projectData.client_name || '',
        project_year: projectData.project_year || new Date().getFullYear(),
        project_duration: projectData.project_duration || '',
        project_url: projectData.project_url || '',
        category: projectData.category || 'Branding',
        tags: projectData.tags || [],
        status: projectData.status || 'completed',
        featured: projectData.featured || false,
        order_index: projectData.order_index || 0,
        active: projectData.active,
        cover_image: null,
        existing_cover_image: projectData.cover_image || '',
        home_image: null,
        existing_home_image: projectData.home_image || '',
        gallery: [],
        existing_gallery: projectData.gallery || [],
        deliverables: projectData.deliverables || [],
        testimonials: projectData.testimonials || []
      });
      
      console.log('Form data set with existing images:', {
        existing_cover_image: projectData.cover_image,
        existing_home_image: projectData.home_image,
        existing_gallery: projectData.gallery
      }); // Debug log
      
      setEditingProject(project);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching project details:', error);
      alert('Error loading project details. Please try again.');
    }
  };

  const handleDelete = async (project) => {
    if (!canDelete) return;
    
    if (window.confirm(`Are you sure you want to delete "${project.title}"?`)) {
      try {
        await axios.delete(`${SERVER}/portfolio/admin/${project.id}`, {
          withCredentials: true
        });
        fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const handleToggleActive = async (project) => {
    if (!canEdit) return;
    
    try {
      await axios.put(`${SERVER}/portfolio/admin/${project.id}`, {
        ...project,
        active: !project.active
      }, {
        withCredentials: true
      });
      fetchProjects();
    } catch (error) {
      console.error('Error toggling project status:', error);
    }
  };
  const openCreateModal = () => {
    if (!canCreate) return;
    
    setFormData({
      title: '',
      slug: '',
      subtitle: '',
      description: '',
      detailed_description: '',
      client_name: '',
      project_year: new Date().getFullYear(),
      project_duration: '',
      project_url: '',
      category: 'Branding',
      tags: [],
      status: 'completed',
      featured: false,
      order_index: 0,
      active: true,
      cover_image: null,
      gallery: [],
      deliverables: [],
      testimonials: []
    });
    setEditingProject(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProject(null);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: generateSlug(title)
    });
  };

  // Helper functions for dynamic fields
  const addTag = () => {
    setFormData({
      ...formData,
      tags: [...formData.tags, '']
    });
  };

  const updateTag = (index, value) => {
    const updatedTags = [...formData.tags];
    updatedTags[index] = value;
    setFormData({
      ...formData,
      tags: updatedTags
    });
  };

  const removeTag = (index) => {
    const updatedTags = formData.tags.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      tags: updatedTags
    });
  };
  const addDeliverable = () => {
    setFormData({
      ...formData,
      deliverables: [...formData.deliverables, { title: '', description: '', icon: '✓' }]
    });
  };

  const updateDeliverable = (index, field, value) => {
    const updatedDeliverables = [...formData.deliverables];
    updatedDeliverables[index][field] = value;
    setFormData({
      ...formData,
      deliverables: updatedDeliverables
    });
  };

  const removeDeliverable = (index) => {
    const updatedDeliverables = formData.deliverables.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      deliverables: updatedDeliverables
    });
  };

  const addTestimonial = () => {
    setFormData({
      ...formData,
      testimonials: [...formData.testimonials, { 
        client_name: '', 
        client_title: '', 
        client_company: '', 
        testimonial_text: '', 
        rating: 5 
      }]
    });
  };

  const updateTestimonial = (index, field, value) => {
    const updatedTestimonials = [...formData.testimonials];
    updatedTestimonials[index][field] = value;
    setFormData({
      ...formData,
      testimonials: updatedTestimonials
    });
  };

  const removeTestimonial = (index) => {
    const updatedTestimonials = formData.testimonials.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      testimonials: updatedTestimonials
    });
  };

  // Section management functions
  const fetchProjectSections = async (projectId) => {
    try {
      setSectionsLoading(true);
      // Use the dedicated admin endpoint to get sections
      const response = await axios.get(`${SERVER}/portfolio/admin/${projectId}/sections`, {
        withCredentials: true
      });
      
      setProjectSections(response.data.sections || []);
    } catch (error) {
      console.error('Error fetching project sections:', error);
      setProjectSections([]);
    } finally {
      setSectionsLoading(false);
    }
  };

  const openSectionManager = async (project) => {
    if (!canEdit) return;
    setManagingSections(project);
    await fetchProjectSections(project.id);
  };

  const closeSectionManager = () => {
    setManagingSections(null);
    setProjectSections([]);
    setShowSectionModal(false);
    setEditingSection(null);
  };

  const openSectionModal = (section = null) => {
    if (section) {
      // Ensure gallery is properly formatted for display
      const existingGallery = section.gallery || [];
      
      setSectionFormData({
        section_title: section.section_title || '',
        section_description: section.section_description || '',
        section_url: section.section_url || '',
        order_index: section.order_index || 0,
        gallery: [], // New files to upload
        existingGallery: existingGallery, // Existing images from server
        highlights: (section.properties || []).map(property => ({
          title: property.property_title || ''
        })), // Map properties to highlights with only title
        section_tags: Array.isArray(section.section_tags) ? section.section_tags : []
      });
      setEditingSection(section);
    } else {
      setSectionFormData({
        section_title: '',
        section_description: '',
        section_url: '',
        order_index: projectSections.length,
        gallery: [],
        existingGallery: [],
        highlights: [],
        section_tags: []
      });
      setEditingSection(null);
    }
    setShowSectionModal(true);
  };

  const handleSectionSubmit = async (e) => {
    e.preventDefault();
    if (!managingSections) return;

    try {
      const formDataToSend = new FormData();
      
      // Append section fields
      formDataToSend.append('section_title', sectionFormData.section_title);
      formDataToSend.append('section_description', sectionFormData.section_description);
      formDataToSend.append('section_url', sectionFormData.section_url);
      formDataToSend.append('order_index', sectionFormData.order_index);
      formDataToSend.append('section_tags', JSON.stringify(sectionFormData.section_tags));
      
      // Map highlights to properties for backend compatibility
      const properties = sectionFormData.highlights.map(highlight => ({
        property_title: highlight.title,
        property_description: '', // Empty since we only have titles
        icon: '✓' // Default icon
      }));
      formDataToSend.append('properties', JSON.stringify(properties));
      
      // Send existing gallery info (for images to keep)
      formDataToSend.append('existingGallery', JSON.stringify(sectionFormData.existingGallery));
      
      // Append new gallery files
      if (sectionFormData.gallery && sectionFormData.gallery.length > 0) {
        sectionFormData.gallery.forEach(file => {
          if (file instanceof File) {
            formDataToSend.append('gallery', file);
          }
        });
      }

      let response;
      if (editingSection) {
        response = await axios.put(
          `${SERVER}/portfolio/admin/${managingSections.id}/sections/${editingSection.id}`, 
          formDataToSend, 
          {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
      } else {
        response = await axios.post(
          `${SERVER}/portfolio/admin/${managingSections.id}/sections`, 
          formDataToSend, 
          {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
      }

      if (response.status === 200 || response.status === 201) {
        await fetchProjectSections(managingSections.id);
        setShowSectionModal(false);
        setEditingSection(null);
        // Reset form data
        setSectionFormData({
          section_title: '',
          section_description: '',
          section_url: '',
          order_index: 0,
          gallery: [],
          existingGallery: [],
          highlights: [],
          section_tags: []
        });
      }
    } catch (error) {
      console.error('Error saving section:', error);
      const errorMessage = error.response?.data?.error || 'Error saving section. Please try again.';
      alert(errorMessage);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!managingSections || !canDelete) return;
    
    if (window.confirm('Are you sure you want to delete this section? This action cannot be undone.')) {
      try {
        await axios.delete(`${SERVER}/portfolio/admin/${managingSections.id}/sections/${sectionId}`, {
          withCredentials: true
        });
        await fetchProjectSections(managingSections.id);
      } catch (error) {
        console.error('Error deleting section:', error);
        const errorMessage = error.response?.data?.error || 'Error deleting section. Please try again.';
        alert(errorMessage);
      }
    }
  };

  // Section highlight helpers
  const addSectionHighlight = () => {
    setSectionFormData({
      ...sectionFormData,
      highlights: [...sectionFormData.highlights, { title: '' }]
    });
  };

  const updateSectionHighlight = (index, field, value) => {
    const updatedHighlights = [...sectionFormData.highlights];
    updatedHighlights[index][field] = value;
    setSectionFormData({
      ...sectionFormData,
      highlights: updatedHighlights
    });
  };

  const removeSectionHighlight = (index) => {
    const updatedHighlights = sectionFormData.highlights.filter((_, i) => i !== index);
    setSectionFormData({
      ...sectionFormData,
      highlights: updatedHighlights
    });
  };

  // Section tags helpers
  const addSectionTag = () => {
    setSectionFormData({
      ...sectionFormData,
      section_tags: [...sectionFormData.section_tags, '']
    });
  };

  const updateSectionTag = (index, value) => {
    const updatedTags = [...sectionFormData.section_tags];
    updatedTags[index] = value;
    setSectionFormData({
      ...sectionFormData,
      section_tags: updatedTags
    });
  };

  const removeSectionTag = (index) => {
    const updatedTags = sectionFormData.section_tags.filter((_, i) => i !== index);
    setSectionFormData({
      ...sectionFormData,
      section_tags: updatedTags
    });
  };

  // Section gallery helpers
  const handleSectionGalleryChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setSectionFormData({
      ...sectionFormData,
      gallery: [...sectionFormData.gallery, ...newFiles]
    });
  };

  const removeExistingImage = (imageIndex) => {
    const updatedExistingGallery = sectionFormData.existingGallery.filter((_, i) => i !== imageIndex);
    setSectionFormData({
      ...sectionFormData,
      existingGallery: updatedExistingGallery
    });
  };

  const removeNewImage = (fileIndex) => {
    const updatedGallery = sectionFormData.gallery.filter((_, i) => i !== fileIndex);
    setSectionFormData({
      ...sectionFormData,
      gallery: updatedGallery
    });
  };

  // Video management functions
  const fetchProjectVideos = async (projectId) => {
    try {
      setVideosLoading(true);
      // Use the dedicated admin endpoint to get project videos
      const response = await axios.get(`${SERVER}/portfolio/admin/${projectId}/videos`, {
        withCredentials: true
      });
      setProjectVideos(response.data || []);
    } catch (error) {
      console.error('Error fetching project videos:', error);
      setProjectVideos([]);
    } finally {
      setVideosLoading(false);
    }
  };

  const fetchSectionVideos = async (sectionId) => {
    try {
      setVideosLoading(true);
      // Use the dedicated admin endpoint to get section videos
      const response = await axios.get(`${SERVER}/portfolio/admin/${managingSections.id}/sections/${sectionId}/videos`, {
        withCredentials: true
      });
      setSectionVideos(response.data || []);
    } catch (error) {
      console.error('Error fetching section videos:', error);
      setSectionVideos([]);
    } finally {
      setVideosLoading(false);
    }
  };

  const openVideoManager = async (project, section = null) => {
    if (!canEdit) return;
    setManagingVideos({ project, section });
    
    if (section) {
      await fetchSectionVideos(section.id);
    } else {
      await fetchProjectVideos(project.id);
    }
    setShowVideoModal(true);
  };

  const closeVideoManager = () => {
    setManagingVideos(null);
    setProjectVideos([]);
    setSectionVideos([]);
    setShowVideoModal(false);
  };

  const handleAddVideo = async (formData) => {
    if (!managingVideos) return;

    try {
      const { project, section } = managingVideos;
      
      if (section) {
        // Add video to section
        await axios.post(`${SERVER}/portfolio/admin/${project.id}/sections/${section.id}/videos`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        await fetchSectionVideos(section.id);
      } else {
        // Add video to project
        await axios.post(`${SERVER}/portfolio/admin/${project.id}/videos`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        await fetchProjectVideos(project.id);
      }
    } catch (error) {
      console.error('Error adding video:', error);
      throw error;
    }
  };

  const handleUpdateVideo = async (videoId, formData) => {
    if (!managingVideos) return;

    try {
      const { project, section } = managingVideos;
      
      if (section) {
        // Update section video
        await axios.put(`${SERVER}/portfolio/admin/${project.id}/sections/${section.id}/videos/${videoId}`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        await fetchSectionVideos(section.id);
      } else {
        // Update project video
        await axios.put(`${SERVER}/portfolio/admin/${project.id}/videos/${videoId}`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        await fetchProjectVideos(project.id);
      }
    } catch (error) {
      console.error('Error updating video:', error);
      throw error;
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!managingVideos) return;

    try {
      const { project, section } = managingVideos;
      
      if (section) {
        // Delete section video
        await axios.delete(`${SERVER}/portfolio/admin/${project.id}/sections/${section.id}/videos/${videoId}`, {
          withCredentials: true
        });
        await fetchSectionVideos(section.id);
      } else {
        // Delete project video
        await axios.delete(`${SERVER}/portfolio/admin/${project.id}/videos/${videoId}`, {
          withCredentials: true
        });
        await fetchProjectVideos(project.id);
      }
    } catch (error) {
      console.error('Error deleting video:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Portfolio Management</h2>
          <p className="text-slate-600">Manage your portfolio projects</p>
        </div>
        {canCreate && (
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <FaPlus />
            <span>Add Project</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              {statuses.map(status => (
                <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Projects Table */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 font-semibold text-slate-700">Project</th>
                <th className="text-left p-4 font-semibold text-slate-700">Category</th>
                <th className="text-left p-4 font-semibold text-slate-700">Status</th>
                <th className="text-left p-4 font-semibold text-slate-700">Client</th>
                <th className="text-left p-4 font-semibold text-slate-700">Year</th>
                <th className="text-right p-4 font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {project.cover_image && (
                        <img
                          src={`${SERVER}${project.cover_image}`}
                          alt={project.title}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      )}
                      <div>
                        <h3 className="font-semibold text-slate-800">{project.title}</h3>
                        <p className="text-sm text-slate-500">{project.slug}</p>
                        {project.subtitle && (
                          <p className="text-sm text-slate-600 mt-1 line-clamp-1">{project.subtitle}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                      {project.category}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(project)}
                        disabled={!canEdit}
                        className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          project.active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        } ${!canEdit ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                      >
                        {project.active ? <FaToggleOn /> : <FaToggleOff />}
                        {project.active ? 'Active' : 'Inactive'}
                      </button>
                      {project.featured && (
                        <FaStar className="text-yellow-500" title="Featured" />
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-slate-600">{project.client_name || 'N/A'}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-slate-600">{project.project_year || 'N/A'}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      {project.project_url && (
                        <a
                          href={project.project_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Project"
                        >
                          <FaExternalLinkAlt />
                        </a>
                      )}
                      <button
                        onClick={() => openSectionManager(project)}
                        disabled={!canEdit}
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Manage Sections"
                      >
                        <FaLayerGroup />
                      </button>
                      <button
                        onClick={() => openVideoManager(project)}
                        disabled={!canEdit}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Manage Videos"
                      >
                        <FaVideo />
                      </button>
                      <button
                        onClick={() => handleEdit(project)}
                        disabled={!canEdit}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(project)}
                        disabled={!canDelete}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Empty State */}
      {projects.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaImage className="text-3xl text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">No Projects Found</h3>
          <p className="text-slate-600 mb-4">
            {searchTerm || filterCategory !== 'all' || filterStatus !== 'all'
              ? 'No projects match your current filters.'
              : 'Start by adding some projects to your portfolio.'}
          </p>
          {canCreate && (
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
            >
              <FaPlus />
              Add Project
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingProject ? 'Edit Project' : 'Add New Project'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Project Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter project title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    URL Slug *
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="project-url-slug"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief project subtitle"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Short Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description for project cards"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Detailed Description
                </label>
                <textarea
                  value={formData.detailed_description}
                  onChange={(e) => setFormData({ ...formData, detailed_description: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Detailed project description for project details page"
                />
              </div>

              {/* Project Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Client Name
                  </label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Client company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Project Year
                  </label>
                  <input
                    type="number"
                    value={formData.project_year}
                    onChange={(e) => setFormData({ ...formData, project_year: parseInt(e.target.value) || new Date().getFullYear() })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="2024"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Duration
                  </label>
                  <input
                    type="text"
                    value={formData.project_duration}
                    onChange={(e) => setFormData({ ...formData, project_duration: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="3 months"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Project URL
                </label>
                <input
                  type="url"
                  value={formData.project_url}
                  onChange={(e) => setFormData({ ...formData, project_url: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com"
                />
              </div>

              {/* Category and Status */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {statuses.map(status => (
                      <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Order Index
                  </label>
                  <input
                    type="number"
                    value={formData.order_index}
                    onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>

                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-semibold text-slate-700">Featured</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-semibold text-slate-700">Active</span>
                  </label>
                </div>
              </div>
              {/* Images */}
              <div className="space-y-6">
                {/* Cover Image */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Cover Image
                  </label>
                  
                  {editingProject && formData.existing_cover_image && (
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 mb-2">Current Cover Image:</p>
                      <div className="relative inline-block">
                        <img
                          src={`${SERVER}${formData.existing_cover_image}`}
                          alt="Current cover"
                          className="w-48 h-32 object-cover rounded-lg border-2 border-slate-300"
                          onError={(e) => {
                            console.error('Failed to load cover image:', `${SERVER}${formData.existing_cover_image}`);
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded shadow">
                          Current
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {formData.cover_image && (
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 mb-2">New Cover Image Preview:</p>
                      <div className="relative inline-block">
                        <img
                          src={URL.createObjectURL(formData.cover_image)}
                          alt="New cover preview"
                          className="w-48 h-32 object-cover rounded-lg border-2 border-blue-300"
                        />
                        <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded shadow">
                          New
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, cover_image: e.target.files[0] })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-sm text-slate-500 mt-2">
                    {editingProject ? 'Leave empty to keep current cover. Upload a new image to replace it.' : 'Upload a cover image for this project'}
                  </p>
                </div>

                {/* Home Image */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Home Page Image
                  </label>
                  
                  {editingProject && formData.existing_home_image && (
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 mb-2">Current Home Page Image:</p>
                      <div className="relative inline-block">
                        <img
                          src={`${SERVER}${formData.existing_home_image}`}
                          alt="Current home image"
                          className="w-48 h-32 object-cover rounded-lg border-2 border-slate-300"
                          onError={(e) => {
                            console.error('Failed to load home image:', `${SERVER}${formData.existing_home_image}`);
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded shadow">
                          Current
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {formData.home_image && (
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 mb-2">New Home Page Image Preview:</p>
                      <div className="relative inline-block">
                        <img
                          src={URL.createObjectURL(formData.home_image)}
                          alt="New home image preview"
                          className="w-48 h-32 object-cover rounded-lg border-2 border-blue-300"
                        />
                        <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded shadow">
                          New
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, home_image: e.target.files[0] })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-sm text-slate-500 mt-2">
                    {editingProject ? 'Leave empty to keep current home image. Upload a new image to replace it.' : 'Upload an image for the home page featured section (optional, defaults to cover image)'}
                  </p>
                </div>

                {/* Gallery Images */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Gallery Images
                  </label>
                  
                  {editingProject && formData.existing_gallery && formData.existing_gallery.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 mb-2">Current Gallery ({formData.existing_gallery.length} images):</p>
                      <div className="grid grid-cols-4 gap-2">
                        {formData.existing_gallery.slice(0, 8).map((img, idx) => (
                          <img
                            key={idx}
                            src={`${SERVER}${img}`}
                            alt={`Gallery ${idx + 1}`}
                            className="w-full h-20 object-cover rounded-lg border border-slate-200"
                            onError={(e) => {
                              console.error('Failed to load gallery image:', `${SERVER}${img}`);
                              e.target.style.opacity = '0.3';
                            }}
                          />
                        ))}
                        {formData.existing_gallery.length > 8 && (
                          <div className="w-full h-20 bg-slate-100 rounded-lg border border-slate-200 flex items-center justify-center text-sm text-slate-600">
                            +{formData.existing_gallery.length - 8} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setFormData({ ...formData, gallery: Array.from(e.target.files) })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-sm text-slate-500 mt-2">
                    {editingProject ? 'Upload new images to add to the gallery (existing images will be kept)' : 'Upload multiple images for the project gallery'}
                  </p>
                </div>
              </div>

              {/* Tags Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-slate-700">
                    Project Tags
                  </label>
                  <button
                    type="button"
                    onClick={addTag}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    <FaPlus />
                    Add Tag
                  </button>
                </div>
                
                <div className="space-y-2">
                  {formData.tags.map((tag, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={tag}
                        onChange={(e) => updateTag(index, e.target.value)}
                        placeholder="Tag name"
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeTag(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              {/* Deliverables Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-slate-700">
                    Project Deliverables
                  </label>
                  <button
                    type="button"
                    onClick={addDeliverable}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    <FaPlus />
                    Add Deliverable
                  </button>
                </div>
                
                <div className="space-y-4">
                  {formData.deliverables.map((deliverable, index) => (
                    <div key={index} className="flex gap-4 items-start p-4 bg-slate-50 rounded-xl">
                      <div className="flex-1 space-y-3">
                        <input
                          type="text"
                          value={deliverable.title}
                          onChange={(e) => updateDeliverable(index, 'title', e.target.value)}
                          placeholder="Deliverable title"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <textarea
                          value={deliverable.description}
                          onChange={(e) => updateDeliverable(index, 'description', e.target.value)}
                          placeholder="Deliverable description (optional)"
                          rows={2}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDeliverable(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              {/* Testimonials Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-slate-700">
                    Client Testimonials
                  </label>
                  <button
                    type="button"
                    onClick={addTestimonial}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    <FaPlus />
                    Add Testimonial
                  </button>
                </div>
                
                <div className="space-y-4">
                  {formData.testimonials.map((testimonial, index) => (
                    <div key={index} className="flex gap-4 items-start p-4 bg-slate-50 rounded-xl">
                      <div className="flex-1 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <input
                            type="text"
                            value={testimonial.client_name}
                            onChange={(e) => updateTestimonial(index, 'client_name', e.target.value)}
                            placeholder="Client name"
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <input
                            type="text"
                            value={testimonial.client_title}
                            onChange={(e) => updateTestimonial(index, 'client_title', e.target.value)}
                            placeholder="Client title"
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <input
                          type="text"
                          value={testimonial.client_company}
                          onChange={(e) => updateTestimonial(index, 'client_company', e.target.value)}
                          placeholder="Client company"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <textarea
                          value={testimonial.testimonial_text}
                          onChange={(e) => updateTestimonial(index, 'testimonial_text', e.target.value)}
                          placeholder="Testimonial text"
                          rows={3}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <select
                          value={testimonial.rating}
                          onChange={(e) => updateTestimonial(index, 'rating', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value={5}>5 Stars</option>
                          <option value={4}>4 Stars</option>
                          <option value={3}>3 Stars</option>
                          <option value={2}>2 Stars</option>
                          <option value={1}>1 Star</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTestimonial(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <FaSave />
                  <span>{editingProject ? 'Update Project' : 'Create Project'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Section Management Modal */}
      {managingSections && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  Manage Sections - {managingSections.title}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openSectionModal()}
                    disabled={!canCreate}
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaPlus />
                    Add Section
                  </button>
                  <button
                    onClick={closeSectionManager}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              {sectionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
                </div>
              ) : projectSections.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaLayerGroup className="text-3xl text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">No Sections Found</h3>
                  <p className="text-slate-600 mb-4">
                    Start by adding sections to make this project dynamic.
                  </p>
                  {canCreate && (
                    <button
                      onClick={() => openSectionModal()}
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300"
                    >
                      <FaPlus />
                      Add First Section
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {projectSections.map((section, index) => (
                    <div key={section.id} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-slate-800 mb-2">
                            {section.section_title}
                          </h4>
                          <p className="text-slate-600 text-sm mb-2">
                            {section.section_description}
                          </p>
                          {section.section_url && (
                            <a
                              href={section.section_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                            >
                              <FaExternalLinkAlt className="text-xs" />
                              View Section
                            </a>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500 bg-slate-200 px-2 py-1 rounded">
                            Order: {section.order_index}
                          </span>
                          <button
                            onClick={() => openSectionModal(section)}
                            disabled={!canEdit}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Edit Section"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => openVideoManager(managingSections, section)}
                            disabled={!canEdit}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Manage Section Videos"
                          >
                            <FaVideo />
                          </button>
                          <button
                            onClick={() => handleDeleteSection(section.id)}
                            disabled={!canDelete}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete Section"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="text-sm font-semibold text-slate-700 mb-2">Gallery ({section.gallery?.length || 0} images)</h5>
                          {section.gallery && section.gallery.length > 0 && (
                            <div className="flex gap-2 overflow-x-auto">
                              {section.gallery.slice(0, 3).map((image, imgIndex) => (
                                <img
                                  key={imgIndex}
                                  src={`${SERVER}${image.image_url}`}
                                  alt={image.image_title || `Section image ${imgIndex + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                                />
                              ))}
                              {section.gallery.length > 3 && (
                                <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center text-xs text-slate-600">
                                  +{section.gallery.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <h5 className="text-sm font-semibold text-slate-700 mb-2">Highlights ({section.properties?.length || 0})</h5>
                          {section.properties && section.properties.length > 0 && (
                            <div className="space-y-1">
                              {section.properties.slice(0, 3).map((property, propertyIndex) => (
                                <div key={propertyIndex} className="text-xs text-slate-600 flex items-center gap-2">
                                  <span className="text-blue-600">{property.icon || '✓'}</span>
                                  {property.property_title}
                                </div>
                              ))}
                              {section.properties.length > 3 && (
                                <div className="text-xs text-slate-500">
                                  +{section.properties.length - 3} more highlights
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div>
                          <h5 className="text-sm font-semibold text-slate-700 mb-2">Tags ({section.section_tags?.length || 0})</h5>
                          {section.section_tags && section.section_tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {section.section_tags.slice(0, 3).map((tag, tagIndex) => (
                                <span
                                  key={tagIndex}
                                  className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs"
                                >
                                  {tag}
                                </span>
                              ))}
                              {section.section_tags.length > 3 && (
                                <span className="text-xs text-slate-500">
                                  +{section.section_tags.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Section Form Modal */}
      {showSectionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingSection ? 'Edit Section' : 'Add New Section'}
                </h3>
                <button
                  onClick={() => setShowSectionModal(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <form onSubmit={handleSectionSubmit} className="p-6 space-y-6">
              {/* Section Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Section Title *
                  </label>
                  <input
                    type="text"
                    value={sectionFormData.section_title}
                    onChange={(e) => setSectionFormData({ ...sectionFormData, section_title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., Branding, Website, Mobile App"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Order Index
                  </label>
                  <input
                    type="number"
                    value={sectionFormData.order_index}
                    onChange={(e) => setSectionFormData({ ...sectionFormData, order_index: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Section Description
                </label>
                <textarea
                  value={sectionFormData.section_description}
                  onChange={(e) => setSectionFormData({ ...sectionFormData, section_description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Describe what this section covers..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Section URL (Optional)
                </label>
                <input
                  type="url"
                  value={sectionFormData.section_url}
                  onChange={(e) => setSectionFormData({ ...sectionFormData, section_url: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="https://example.com"
                />
              </div>

              {/* Section Gallery */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Section Gallery Images
                </label>
                
                {/* Existing Images */}
                {sectionFormData.existingGallery.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-slate-600 mb-2">Current Images ({sectionFormData.existingGallery.length})</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {sectionFormData.existingGallery.map((image, index) => {
                        // Handle both object with image_url and direct string path
                        const imagePath = typeof image === 'string' ? image : (image.image_url || image);
                        const imageTitle = typeof image === 'object' ? image.image_title : null;
                        
                        return (
                          <div key={index} className="relative group">
                            <img
                              src={`${SERVER}${imagePath}`}
                              alt={imageTitle || `Gallery image ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border border-slate-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeExistingImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-all duration-200 shadow-lg"
                              style={{ 
                                opacity: 1,
                                transform: 'scale(1)',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                              title="Remove image"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* New Images Preview */}
                {sectionFormData.gallery.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-slate-600 mb-2">New Images to Upload</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {sectionFormData.gallery.map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`New upload ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border border-slate-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeNewImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-all duration-200 shadow-lg"
                            style={{ 
                              opacity: 1,
                              transform: 'scale(1)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                            title="Remove image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* File Upload */}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleSectionGalleryChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Select multiple images to add to the gallery
                </p>
              </div>

              {/* Section Highlights */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-slate-700">
                    Section Highlights
                  </label>
                  <button
                    type="button"
                    onClick={addSectionHighlight}
                    className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    <FaPlus />
                    Add Highlight
                  </button>
                </div>
                
                <div className="space-y-4">
                  {sectionFormData.highlights.map((highlight, index) => (
                    <div key={index} className="flex gap-4 items-center p-4 bg-slate-50 rounded-xl">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={highlight.title}
                          onChange={(e) => updateSectionHighlight(index, 'title', e.target.value)}
                          placeholder="Highlight title"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSectionHighlight(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section Tags */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-slate-700">
                    Section Tags ({sectionFormData.section_tags.length})
                  </label>
                  <button
                    type="button"
                    onClick={addSectionTag}
                    className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    <FaPlus />
                    Add Tag
                  </button>
                </div>
                
                {sectionFormData.section_tags.length === 0 ? (
                  <div className="text-sm text-slate-500 italic p-4 bg-slate-50 rounded-lg">
                    No tags added yet. Click "Add Tag" to add section tags.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sectionFormData.section_tags.map((tag, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={tag}
                          onChange={(e) => updateSectionTag(index, e.target.value)}
                          placeholder="Tag name"
                          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        <button
                          type="button"
                          onClick={() => removeSectionTag(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowSectionModal(false)}
                  className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <FaSave />
                  <span>{editingSection ? 'Update Section' : 'Create Section'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Video Management Modal */}
      {showVideoModal && managingVideos && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  Manage Videos - {managingVideos.project.title}
                  {managingVideos.section && ` - ${managingVideos.section.section_title}`}
                </h3>
                <button
                  onClick={closeVideoManager}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <div className="p-6">
              {videosLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
                </div>
              ) : (
                <VideoManager
                  videos={managingVideos.section ? sectionVideos : projectVideos}
                  onAdd={handleAddVideo}
                  onUpdate={handleUpdateVideo}
                  onDelete={handleDeleteVideo}
                  canEdit={canEdit}
                  canCreate={canCreate}
                  canDelete={canDelete}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;